import i18nModule from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import en from "@src/locales/en.json";
import de from "@src/locales/de.json";

/**
 * Unwraps the i18next instance from what module federation hands us.
 *
 * `@originjs/vite-plugin-federation` resolves a shared module to its ES module
 * NAMESPACE, not to its default export: the built code is
 * `const i18n = await importShared("i18next")`. i18next's namespace re-exports a
 * handful of instance methods bound to the singleton (`use`, `init`, `t`,
 * `changeLanguage`), which is why `i18n.use(...).init(...)` keeps appearing to
 * work and hides this. It does NOT carry `on`, `isInitialized` or
 * `addResourceBundle`.
 *
 * Two consequences, both of which this module had:
 *
 *  1. `i18n.isInitialized` is always `undefined`, so the "only init when
 *     standalone" guard never fires inside the shell and this module
 *     re-initialises the shared instance, replacing the resources of every
 *     module that loaded before it. Exactly what the guard exists to prevent.
 *  2. Passing the namespace to `<I18nextProvider i18n={...}>` throws
 *     `TypeError: i18n?.on is not a function`, because react-i18next subscribes
 *     with `i18n.on(...)`.
 *
 * The other four frontends carry this same unwrap (AB#765527); the scaffold
 * template still ships the unpatched version.
 */
export function resolveI18nInstance<T>(module: T): T {
  const candidate = module as { on?: unknown; default?: unknown };

  // Already the instance (standalone dev, and under vitest).
  if (typeof candidate?.on === "function") return module;

  // A namespace from importShared: the instance is the default export.
  const unwrapped = candidate?.default;
  if (typeof (unwrapped as { on?: unknown } | undefined)?.on === "function") {
    return unwrapped as T;
  }

  // Neither shape. Return it unchanged so the failure surfaces where it is
  // caused rather than as a confusing undefined further downstream.
  return module;
}

/**
 * i18n for the Releases module.
 *
 * The module owns the `releases` namespace rather than merging into the shared
 * default `translation` one, where keys collide silently across modules. Call
 * sites are unchanged: each mount point wraps its tree in
 * `<I18nextProvider defaultNS={I18N_NAMESPACE}>`, and react-i18next resolves a
 * bare `useTranslation()` as `ns || defaultNS-from-context ||
 * i18n.options.defaultNS`, so every `t("...")` reads from `releases` without a
 * per-call-site edit.
 *
 * The shell and BeeHive's UATAuthProvider keep `translation`. Do not add module
 * keys there.
 */
export const I18N_NAMESPACE = "releases";

const i18n = resolveI18nInstance(i18nModule);

if (!i18n.isInitialized) {
  i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources: {
        en: { [I18N_NAMESPACE]: en },
        de: { [I18N_NAMESPACE]: de },
      },
      defaultNS: I18N_NAMESPACE,
      fallbackLng: "en",
      interpolation: { escapeValue: false },
      react: { useSuspense: true },
    });
} else {
  i18n.addResourceBundle("en", I18N_NAMESPACE, en, true, true);
  i18n.addResourceBundle("de", I18N_NAMESPACE, de, true, true);
}

export default i18n;
